import './setupGlobals'; // must be first — sets window.moment / window.jQuery for legacy plugins

import { Fragment, PureComponent, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { connect, Provider } from 'react-redux';
import { bindActionCreators } from 'redux';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { PersistGate } from 'redux-persist/integration/react';
import FullStory from 'react-fullstory';
import 'core-js/stable';
import axios from 'axios';
import qs from 'qs';
import * as Sentry from '@sentry/react';

// Vendor + global CSS must be imported before any local module that pulls in
// component-level `.scss` (i.e. before `./routing`). Vite injects each imported
// stylesheet as its own <style> in module-evaluation order, and ESM evaluates
// imports top-to-bottom depth-first — so if these sat below `import './routing'`
// the component SCSS would land *before* semantic.min.css in <head>, and
// same-specificity / same-!important overrides in e.g. activities.scss would
// lose the source-order tiebreak to Semantic UI. (webpack's CSS extraction
// ordered entry CSS first regardless; Vite does not.)
import 'bootstrap/dist/css/bootstrap.css';
import 'react-notifications/lib/notifications.css';
import 'semantic-ui-css/semantic.min.css';
import 'ckeditor5/ckeditor5.css';
import './index.scss';

import i18next from './i18n';

import {
  getRefreshToken,
  setAuthenticatedToken,
  setRefreshToken,
} from './utils/cacheStore';

import {
  BASE_URL,
  STAGE_BASIC_AUTH_USER,
  STAGE_BASIC_AUTH_PASSWORD,
} from './utils/constant';

// import SentryBoundary from './utils/errorBoundary';
import { get } from './utils/lodash';
import AppRouting from './routing';
import { unregister } from './serviceWorker';
import { store, persistor } from './store';
import { initializedApp } from './store/actions';
import { userAuthMgr } from './utils/auth';
import NetworkDetectorHoc from './lib/networkDetectorHoc';

// Suppress the benign "ResizeObserver loop" browser warning.
// CRA's dev overlay treats it as a fatal error but it is harmless — it fires
// when ResizeObserver callbacks (e.g. Vega-Lite charts) can't be delivered
// within a single animation frame. Production builds are unaffected.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (
      e.message ===
        'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded'
    ) {
      e.stopImmediatePropagation();
    }
  });
}

store.dispatch(initializedApp());

// to save request type [get, post, put, ...]
let requestMethod = null;
axios.defaults.baseURL = BASE_URL;
axios.defaults.timeout = 60000;
axios.defaults.headers.common['Pragma'] = 'no-cache';

// stage API is gated by Basic Auth as a front-door lock, separate from the
// app's own JWT Bearer auth below - sent on a dedicated header (not
// Authorization) so it survives login/token refresh without being clobbered
if (STAGE_BASIC_AUTH_USER && STAGE_BASIC_AUTH_PASSWORD) {
  axios.defaults.headers.common['x-stage-auth'] =
    'Basic ' + btoa(`${STAGE_BASIC_AUTH_USER}:${STAGE_BASIC_AUTH_PASSWORD}`);
}

axios.interceptors.request.use((request) => {
  requestMethod = get(request, 'method', 'get');
  request.ts = performance.now(); // to find the performance

  // pass the current language in the Accept-Language header
  request.headers['Accept-Language'] = i18next.language;

  // encode form data
  if (
    request.data &&
    request.headers['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    request.data = qs.stringify(request.data);
  }

  return request;
});

let isRefreshing = false;
let requestQueue = [];

// network error vars
let isShownNetworkError = false;
let networkErrorPathname = '';

const AxiosInterceptor = ({ children }) => {
  const navigate = useNavigate();
  function trackApiResponse(response, ts) {
    if (response.config && response.config.url) {
      ReactGA.event({
        category: 'Axios',
        action: response.config.url,
        value: ts, // in milliseconds
        label: response.status,
      });
    }
  }

  // this function executes all the held functions in request queue array
  const processQueue = (error, token = null) => {
    requestQueue.forEach(async (promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(token);
      }
    });
    requestQueue = [];
  };

  // log out user
  const userLogOut = async () => {
    await userAuthMgr.signout(() => {
      persistor.purge();
    });
    const pathname = window.location.pathname;
    navigate('/', {
      state: {
        redirectRoute: pathname,
        logoutReason: 'Session Expired',
      },
    });
  };

  axios.interceptors.response.use(
    (response) => {
      const ts = Number(performance.now() - response.config.ts);
      trackApiResponse(response, ts);
      return response;
    },
    async (error) => {
      // network error check
      if (error.message === 'Network Error') {
        if (
          (!isShownNetworkError && networkErrorPathname === '') ||
          (networkErrorPathname !== '' &&
            networkErrorPathname !== window.location.pathname)
        ) {
          isShownNetworkError = true;
          networkErrorPathname = window.location.pathname;
          return Promise.reject('NETWORK_ERROR');
        }

        // discard API calls due to network error; no need to call this again
        if (requestMethod && requestMethod.toUpperCase() === 'GET') {
          return Promise.reject('NETWORK_DISCARD');
        } else {
          return Promise.reject('NETWORK_ERROR');
        }
      }

      const ts = error.config ? Number(performance.now() - error.config.ts) : 0;
      trackApiResponse(error, ts);

      const status = get(error, 'response.status', 500);
      const msg =
        get(error, 'response.data.message', null) ||
        get(error, 'response.data', null) ||
        get(error, 'statusText', null);
      const originalRequest = error.config;

      console.warn(`AXIOS RESPONSE ERROR status: ${status} msg: ${msg}`);
      console.dir(error.response?.headers, { depth: 3 });

      if (status === 401) {
        // Unauthorized - could be a number of reasons
        // The passport middleware on the Service API now returns all
        // information in the www-authenticate header, which is exposed via CORS.
        // This header has 3 fields, error code, error code label, and description, separated by semicolons.
        // For example:
        //    "-10002; E_AUTH_DISABLED; The account is disabled."
        // So split up the header value, and go from there.

        // check for a failure when refreshing our auth token
        if (originalRequest.url === `${BASE_URL}/auth/refresh`) {
          await userLogOut();
          return Promise.reject(error);
        }

        const hdr = error.response.headers['www-authenticate'] || '';
        console.warn(`www-authenticate: ${hdr}`);
        const fields = hdr.split(';').map((fld) => fld.trim());
        if (fields.length === 3) {
          const errorCodeName = fields[1];

          // check for disabled user account
          if (errorCodeName === 'E_AUTH_DISABLED') {
            await userAuthMgr.signout(() => persistor.purge());
            navigate('/account-disabled');
            return Promise.reject(error);
          }

          // check for auth token expiry
          if (
            !originalRequest._retry &&
            errorCodeName === 'E_AUTH_SESSION_EXPIRED'
          ) {
            // while re-generating refresh token all the other request are held,
            // once refresh token generated send the request to API
            if (isRefreshing) {
              return new Promise(function (resolve, reject) {
                // add request to queue
                requestQueue.push({ resolve, reject });
              })
                .then((token) => {
                  originalRequest.headers['Authorization'] = 'Bearer ' + token;
                  return axios(originalRequest);
                })
                .catch(async (err) => {
                  await userLogOut();
                  return Promise.reject(error);
                });
            }

            // have to ask backend for a refreshed token
            originalRequest._retry = true;
            isRefreshing = true;
            const refresh_token = await getRefreshToken();
            return new Promise((resolve, reject) => {
              axios
                .post('/auth/refresh', { refresh_token })
                .then((res) => {
                  const refreshToken = res.data.refreshToken;
                  const token = res.data.token;
                  setAuthenticatedToken(token);
                  setRefreshToken(refreshToken);
                  axios.defaults.headers.common['Authorization'] =
                    'Bearer ' + token;
                  originalRequest.headers['Authorization'] = 'Bearer ' + token;
                  // send token to run paused requests
                  processQueue(null, token);
                  resolve(axios(originalRequest));
                })
                .catch((err) => {
                  processQueue(err, null);
                  reject(err);
                })
                .then(() => {
                  // finally release request queue for call requests
                  isRefreshing = false;
                });
            });
          }
        }
      }

      // some other kind of error
      return Promise.reject(error);
    }
  );

  return children;
};

class IndexApp extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      view: false,
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error });
    unregister();
  }

  async componentDidMount() {
    try {
      unregister();
      Sentry.init({
        enabled: import.meta.env.REACT_APP_ENV_VALUE === 'production',
        environment: 'production',
        debug: false,
        dsn: 'https://4536a50f286842c3bb708c1cbae2bcab@o261360.ingest.sentry.io/1459144',
        integrations: [
          new Sentry.BrowserTracing(),
          new Sentry.Replay({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [
              window.location.origin,
              BASE_URL,
              /^\/(?!\/)/,
            ],
          }),
        ],
        beforeSend(event, hint) {
          // Check if it is an exception, and if so, show the report dialog
          if (event.exception) {
            Sentry.showReportDialog({ eventId: event.event_id });
          }
          return event;
        },
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.1,
        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,
        // integrations: [new Sentry.Replay()],
      });

      ReactGA.initialize('UA-127298113-6', {
        debug: false,
        // import.meta.env.REACT_APP_ENV_VALUE === 'production' ? false : true,
        titleCase: false,
      });
      this.setState({ view: true });
    } catch (error) {
      this.setState({ view: true });
    }
  }

  render() {
    const { view } = this.state;
    if (view) {
      return (
        <Fragment>
          <FullStory
            org="o-19SSJE-na1"
            debug={import.meta.env.REACT_APP_ENV_VALUE !== 'production'}
          />
          {this.props.children}
        </Fragment>
      );
    } else {
      return null;
    }
  }
}

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

const mapStateToProps = (state) => {
  return state;
};

const AppWrapper = connect(mapStateToProps, mapDispatchToProps)(IndexApp);
const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  // <SentryBoundary>
  <StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter>
          <AxiosInterceptor>
            <AppWrapper>
              <NetworkDetectorHoc>
                <AppRouting />
              </NetworkDetectorHoc>
            </AppWrapper>
          </AxiosInterceptor>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </StrictMode>
  // </SentryBoundary>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
unregister();
